import React, { useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import UpdatedPlayerCombineData from './UpdatedPlayerCombineData';
import UpdatedPlayerProCombineData from './UpdatedPlayerProCombineData';
import UpdatedPlayerMultiSportData from './UpdatedPlayerMultiSportData';
import { getScoreValue } from '../atoms/Score';
import gql from '../services/gql';
import Position from '../types/Position';
import Team from '../types/Team';
import Player from '../types/Player';
import PlayerPAIStats, { PlayerPAIStatsComparison } from '../types/PlayerPAIStats';
import { PlayerEventStatsComparison } from '../types/StatsPlayer';
import PlayerNFLPAIStats from '../types/PlayerNFLPAIStats';
import PlayerCombineStats from '../types/PlayerCombineStats';
import PlayerProCombineStats from '../types/PlayerProCombineStats';
import PlayerCombineEvent from '../types/PlayerCombineEvent';
import PlayerNFLCombineEvent from '../types/PlayerNFLCombineEvent';
import PlayerPPIStats, { UpdatedPlayerPPIStat } from '../types/PlayerPPIStats';
import PlayerNFLPPIStats from '../types/PlayerNFLPPIStats';
import UpdatedPlayerCharts from './UpdatedPlayerCharts';
import { TEAM_TYPE } from '../templates/UpdatedPlayerPageTemplate';
import Column from '../types/Column';
import { updateUserProfileStatsCategories, updateUserProfileStatsCategoriesNFL } from '../redux/dispatchers/user-settings';
import { State } from '../redux/reducers';
import UpdatedPlayerTrackAndFieldData from './UpdatedPlayerTrackAndFieldData';
import {
  getNormalizedProfileStatsCategories,
  categoriesToColumns,
  getProfileStatsCategoryOptions,
} from '../services/profile-stats-categories';

interface UpdatedPlayerProfileStatsProps {
  player?: Player;
  collegeTeamId?: number;
  collegeTeam?: Team;
  nflTeamId?: number;
  nflTeam?: Team;
  position?: Position;
  isNFL: boolean;
  selectedTeamType?: string;
  requestedTabValue?: string;
  requestedTabKey?: number;
}

export const TAB = {
  PAI: 0,
  COMBINE: 1,
  MULTISPORT: 2,
  PPI: 3,
  PRO_COMBINE: 4,
};
export const TABS_VALUES_ENUM = {
  PAI: 'pai',
  COMBINE: 'combine',
  MULTISPORT: 'multisport',
  PPI: 'ppi',
  PRO_COMBINE: 'proCombine',
};

const COLLEGE_PAI_COMPARISON_STATS_QUERY = `
  performance
  event
  meet
  year
  date
  comparisonStats {
    percentile
    playerRank
    playerCount
  }
`;
const COLLEGE_PAI_STATS_QUERY = `
  performance
  event
  meet
  year
  date
  hsClassStats {
    percentile
    playerRank
    playerCount
  }
  div1Stats {
    percentile
    playerRank
    playerCount
  }
  teamStats {
    percentile
    playerRank
    playerCount
  }
  conferenceStats {
    percentile
    playerRank
    playerCount
  }
  nflDraftStats{
    percentile
    playerRank
    playerCount
  }
`;
const NFL_PAI_COMPARISON_STATS_QUERY = `
  performance
  event
  meet
  year
  comparisonStats {
    percentile
    playerRank
    playerCount
  }
`;
const NFL_PAI_STATS_QUERY = `
  performance
  event
  meet
  year
  collegeTeamStats {
    percentile
    playerRank
    playerCount
  }
  collegeConferenceStats {
    percentile
    playerRank
    playerCount
  }
  nflTeamStats {
    percentile
    playerRank
    playerCount
  }
  nflUDFAStats {
    percentile
    playerRank
    playerCount
  }
  nflDraftStats {
    percentile
    playerRank
    playerCount
  }
`;

export default function UpdatedPlayerProfileStats (props: UpdatedPlayerProfileStatsProps) {
  const {
    player,
    collegeTeamId,
    collegeTeam,
    nflTeamId,
    nflTeam,
    position,
    isNFL,
    selectedTeamType,
    requestedTabValue,
    requestedTabKey,
  } = props;
  const dispatch = useDispatch();
  const profileStatsCategories = useSelector((state: State) => state.userSettings.profileStatsCategories);
  const profileStatsCategoriesNFL = useSelector((state: State) => state.userSettings.profileStatsCategoriesNFL);
  const selectedProfileStatsCategories = isNFL ? profileStatsCategoriesNFL : profileStatsCategories;
  const selectedProfileStatsCategoriesKey = useMemo(() => (selectedProfileStatsCategories || []).join('|'), [selectedProfileStatsCategories]);
  const normalizedProfileStatsCategories = useMemo(() => getNormalizedProfileStatsCategories(selectedProfileStatsCategories, isNFL), [selectedProfileStatsCategoriesKey, isNFL]);
  const normalizedProfileStatsCategoriesKey = useMemo(() => normalizedProfileStatsCategories.join('|'), [normalizedProfileStatsCategories]);
  const categoriesQueryValue = useMemo(() => normalizedProfileStatsCategories.map((category:string) => `"${category}"`).join(', '), [normalizedProfileStatsCategoriesKey]);
  const profileStatsCategoriesOptions = getProfileStatsCategoryOptions(isNFL);

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState(TAB.PAI);
  const [selectedTabValue, setSelectedTabValue] = useState<string>('');
  const [columns, setColumns] = useState<Column[]>(categoriesToColumns(normalizedProfileStatsCategories, isNFL));

  // const [paiStats, setPAIStats] = useState<PlayerPAIStats | PlayerNFLPAIStats>();
  const [paiStats, setPAIStats] = useState<PlayerEventStatsComparison>();
  const [sprints, setSprints] = useState<Array<PlayerEventStatsComparison>>([]);
  const [relays, setRelays] = useState<Array<PlayerEventStatsComparison>>([]);
  const [hurdles, setHurdles] = useState<Array<PlayerEventStatsComparison>>([]);
  const [jumps, setJumps] = useState<Array<PlayerEventStatsComparison>>([]);
  const [throws, setThrows] = useState<Array<PlayerEventStatsComparison>>([]);

  const [combineStats, setCombineStats] = useState<PlayerEventStatsComparison>();
  const [combineEvents, setCombineEvents] = useState<Array<PlayerCombineEvent | PlayerNFLCombineEvent>>([]);

  const [proCombineStats, setProCombineStats] = useState<PlayerEventStatsComparison>();
  const [nflCombineEvents, setNFLCombineEvents] = useState<Array<PlayerCombineEvent | PlayerNFLCombineEvent>>([]);

  const [ppiStats, setPPIStats] = useState<UpdatedPlayerPPIStat>();

  const hasPaiData = !!player?.pai && getScoreValue(player.pai, { position }) !== null;
  const hasCombineScore = !!player?.combine && getScoreValue(player.combine, { position }) !== null;
  const hasProCombineData = !!player?.nflCombines?.length;
  const hasHSCombineData = !!player?.hsCombines?.length;
  const hasPPIScore = !!player?.ppi;

  const getTabIndexByValue = (tabValue: string) => {
    switch (tabValue) {
      case TABS_VALUES_ENUM.PAI:
        return TAB.PAI;
      case TABS_VALUES_ENUM.COMBINE:
        return TAB.COMBINE;
      case TABS_VALUES_ENUM.MULTISPORT:
        return TAB.MULTISPORT;
      case TABS_VALUES_ENUM.PPI:
        return TAB.PPI;
      case TABS_VALUES_ENUM.PRO_COMBINE:
        return TAB.PRO_COMBINE;
      default:
        return TAB.PAI;
    }
  };

  useEffect(() => {
    setColumns(categoriesToColumns(normalizedProfileStatsCategories, isNFL));
  }, [normalizedProfileStatsCategoriesKey, isNFL]);

  useEffect(() => {
    if (!requestedTabValue) return;

    setSelectedTabValue(requestedTabValue);
    setSelectedTab(getTabIndexByValue(requestedTabValue));
  }, [requestedTabKey, requestedTabValue]);

  useEffect(() => {
    if (player?.id && position) {
      let selectedTab = selectedTeamType === TEAM_TYPE.HIGH_SCHOOL ? TABS_VALUES_ENUM.MULTISPORT : TABS_VALUES_ENUM.PPI;

      if (hasCombineScore || hasHSCombineData) {
        selectedTab = TABS_VALUES_ENUM.COMBINE;
      }

      if (hasPaiData) {
        selectedTab = TABS_VALUES_ENUM.PAI;
      }

      setSelectedTabValue(selectedTab);
    }
  }, [player?.id, position, hasPaiData, hasCombineScore, hasHSCombineData]);

  useEffect(() => {
    if (player?.id) {
      if (isNFL) {
        if (selectedTabValue === TABS_VALUES_ENUM.PAI) {
          // fetchNFLPAIStats();
          fetchNFLPAIStatsComparison();
        }
        if ((selectedTabValue === TABS_VALUES_ENUM.COMBINE && (hasCombineScore || hasHSCombineData))
          || (selectedTabValue === TABS_VALUES_ENUM.PRO_COMBINE && hasProCombineData)
        ) {
          // fetchNFLCombineStats();
          fetchNFLCombineStatsComparison();
        }
        if (selectedTabValue === TABS_VALUES_ENUM.PPI) {
          fetchNFLPPIStats();
        }
      } else if (!isNFL) {
        if (selectedTabValue === TABS_VALUES_ENUM.PAI) {
          // fetchPAIStats();
          fetchPAIStatsComparison();
        }
        if (selectedTabValue === TABS_VALUES_ENUM.COMBINE && (hasCombineScore || hasHSCombineData)
        || (selectedTabValue === TABS_VALUES_ENUM.PRO_COMBINE && hasProCombineData)
        ) {
          // fetchCombineStats();
          fetchCombineStatsComparison();
        }
        if (selectedTabValue === TABS_VALUES_ENUM.PPI) {
          fetchPPIStats();
        }
      }
    }
  }, [selectedTabValue, player?.id, collegeTeamId, nflTeamId, position, isNFL, normalizedProfileStatsCategoriesKey]);

  function fetchPAIStatsComparison () {
    if (!player?.id || !collegeTeamId || !position) return;
    if (!loading) setLoading(true);

    gql(`
      getPAIStats (
        playerId: ${player.id},
        compareToTeamId: ${collegeTeamId},
        positionCode: "${position}",
        categories: [${categoriesQueryValue}]
      ) {
        paiStats {
          performance
          event
          meet
          year
          date
          comparisonStats {
            percentile
            playerRank
            playerCount
          }
        }
        sprints {
          ${COLLEGE_PAI_COMPARISON_STATS_QUERY}
        }
        relays {
          ${COLLEGE_PAI_COMPARISON_STATS_QUERY}
        }
        hurdles {
          ${COLLEGE_PAI_COMPARISON_STATS_QUERY}
        }
        jumps {
          ${COLLEGE_PAI_COMPARISON_STATS_QUERY}
        }
        throws {
          ${COLLEGE_PAI_COMPARISON_STATS_QUERY}
        }
      }
    `)
      .then((data:any) => ({
        paiStats: get(data, 'getPAIStats.paiStats') as PlayerEventStatsComparison,
        sprints: get(data, 'getPAIStats.sprints', []) as PlayerEventStatsComparison[],
        relays: get(data, 'getPAIStats.relays', []) as PlayerEventStatsComparison[],
        hurdles: get(data, 'getPAIStats.hurdles', []) as PlayerEventStatsComparison[],
        jumps: get(data, 'getPAIStats.jumps', []) as PlayerEventStatsComparison[],
        throws: get(data, 'getPAIStats.throws', []) as PlayerEventStatsComparison[],
      }))
      .then(({ paiStats, sprints, relays, hurdles, jumps, throws }:{ paiStats:PlayerEventStatsComparison, sprints:PlayerEventStatsComparison[], relays:PlayerEventStatsComparison[], hurdles:PlayerEventStatsComparison[], jumps:PlayerEventStatsComparison[], throws:PlayerEventStatsComparison[] }) => {
        setPAIStats(paiStats);
        setSprints(sprints.filter(sprint => !!sprint.performance));
        setRelays(relays.filter(relay => !!relay.performance));
        setHurdles(hurdles.filter(hurdle => !!hurdle.performance));
        setJumps(jumps.filter(jump => !!jump.performance));
        setThrows(throws.filter(throwEvent => !!throwEvent.performance));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  function fetchNFLPAIStatsComparison () {
    if (!player?.id || !collegeTeamId || !nflTeamId || !position) return;
    if (!loading) setLoading(true);

    gql(`
      getPAIStatsNFL (
        playerId: ${player.id},
        compareToCollegeTeamId: ${collegeTeamId},
        compareToNFLTeamId: ${nflTeamId},
        positionCode: "${position}",
        categories: [${categoriesQueryValue}]
      ) {
        paiStats {
          performance
          event
          meet
          year
          comparisonStats {
            percentile
            playerRank
            playerCount
          }
        }
        sprints {
          ${NFL_PAI_COMPARISON_STATS_QUERY}
        }
        relays {
          ${NFL_PAI_COMPARISON_STATS_QUERY}
        }
        hurdles {
          ${NFL_PAI_COMPARISON_STATS_QUERY}
        }
        jumps {
          ${NFL_PAI_COMPARISON_STATS_QUERY}
        }
        throws {
          ${NFL_PAI_COMPARISON_STATS_QUERY}
        }
      }
    `)
      .then((data:any) => ({
        paiStats: get(data, 'getPAIStatsNFL.paiStats') as PlayerEventStatsComparison,
        sprints: get(data, 'getPAIStatsNFL.sprints', []) as PlayerEventStatsComparison[],
        relays: get(data, 'getPAIStatsNFL.relays', []) as PlayerEventStatsComparison[],
        hurdles: get(data, 'getPAIStatsNFL.hurdles', []) as PlayerEventStatsComparison[],
        jumps: get(data, 'getPAIStatsNFL.jumps', []) as PlayerEventStatsComparison[],
        throws: get(data, 'getPAIStatsNFL.throws', []) as PlayerEventStatsComparison[],
      }))
      .then(({ paiStats, sprints, relays, hurdles, jumps, throws }:{ paiStats:PlayerEventStatsComparison, sprints:PlayerEventStatsComparison[], relays:PlayerEventStatsComparison[], hurdles:PlayerEventStatsComparison[], jumps:PlayerEventStatsComparison[], throws:PlayerEventStatsComparison[] }) => {
        setPAIStats(paiStats);
        setSprints(sprints.filter(sprint => !!sprint.performance));
        setRelays(relays.filter(relay => !!relay.performance));
        setHurdles(hurdles.filter(hurdle => !!hurdle.performance));
        setJumps(jumps.filter(jump => !!jump.performance));
        setThrows(throws.filter(throwEvent => !!throwEvent.performance));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  function fetchCombineStatsComparison () {
    if (!player?.id || !collegeTeamId || !position) return;
    if (!loading) setLoading(true);

    gql(`
      getCombineStats (
        playerId: ${player.id},
        compareToTeamId: ${collegeTeamId},
        positionCode: "${position}",
        categories: [${categoriesQueryValue}]
      ) {
        combineStats {
          performance
          event
          meet
          year
          comparisonStats {
            percentile
            playerRank
            playerCount
          }
        }

        proCombineStats {
          performance
          event
          meet
          year

          comparisonStats {
            percentile
            playerRank
            playerCount
          }
        }

        combines {
          id
          type
          city
          combineDate
          height
          weight
          category
          combineHasFreshmanData
          combine

          eventStats {
            performance
            event
            meet
            year
            comparisonStats {
              percentile
              playerRank
              playerCount
            }
          }
        }
      }
    `)
      .then((data:any) => ({
        combineStats: get(data, 'getCombineStats.combineStats') as PlayerEventStatsComparison,
        combineEvents: get(data, 'getCombineStats.combines') as PlayerCombineEvent[],
        proCombineStats: get(data, 'getCombineStats.proCombineStats') as PlayerEventStatsComparison,
      }))
      .then(({ combineStats, proCombineStats, combineEvents }:{ combineStats:PlayerEventStatsComparison, proCombineStats:PlayerEventStatsComparison, combineEvents:PlayerCombineEvent[] }) => {
        setCombineStats(combineStats);
        setProCombineStats(proCombineStats);
        setCombineEvents(combineEvents?.filter(event => event.category !== 'nfl'));
        setNFLCombineEvents(combineEvents?.filter(event => event.category === 'nfl'));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  function fetchNFLCombineStatsComparison () {
    if (!player?.id || !collegeTeamId || !nflTeamId || !position) return;
    if (!loading) setLoading(true);

    gql(`
      getCombineStatsNFL (
        playerId: ${player.id},
        compareToCollegeTeamId: ${collegeTeamId},
        compareToNFLTeamId: ${nflTeamId},
        positionCode: "${position}",
        categories: [${categoriesQueryValue}]
      ) {
        combineStats {
          performance
          event
          meet
          year
          comparisonStats {
            percentile
            playerRank
            playerCount
          }
        }

        proCombineStats {
          performance
          event
          meet
          year

          comparisonStats {
            percentile
            playerRank
            playerCount
          }
        }

        combines {
          id
          type
          city
          combineDate
          height
          weight
          category

          eventStats {
            performance
            event
            meet
            year
            comparisonStats {
              percentile
              playerRank
              playerCount
            }
          }
        }
      }
    `)
      .then((data:any) => ({
        combineStats: get(data, 'getCombineStatsNFL.combineStats') as PlayerEventStatsComparison,
        proCombineStats: get(data, 'getCombineStatsNFL.proCombineStats') as PlayerEventStatsComparison,
        combineEvents: get(data, 'getCombineStatsNFL.combines') as PlayerNFLCombineEvent[],
      }))
      .then(({ combineStats, proCombineStats, combineEvents }:{ combineStats:PlayerEventStatsComparison, proCombineStats:PlayerEventStatsComparison, combineEvents:PlayerNFLCombineEvent[] }) => {
        setCombineStats(combineStats);
        setProCombineStats(proCombineStats);
        setCombineEvents(combineEvents.filter(event => event.category !== 'nfl'));
        setNFLCombineEvents(combineEvents.filter(event => event.category === 'nfl'));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  const StatsItem = `{
      percentile
      playerRank
      playerCount 
    }`;
  const PPIStats = `{
    ppi
    comparisonStats ${StatsItem}
  }`;
  
  function fetchPPIStats () {
    if (!player?.id || !collegeTeamId || !position) return;
    if (!loading) setLoading(true);

    gql(`
      getPPIStats (
        playerId: ${player.id},
        compareToTeamId: ${collegeTeamId},
        positionCode: "${position}",
        categories: [${categoriesQueryValue}]
      ) {
        yearlyStats {
          year
          overallPPI ${PPIStats}
          passingPPI ${PPIStats}
          receivingPPI ${PPIStats}
          rushingPPI ${PPIStats}
          blockPPI ${PPIStats}
          defensePPI ${PPIStats}

          position
          overallTotalCount
          passingTotalCount
          rushingTotalCount
          receivingTotalCount
          defenseTotalCount
          overallRank
          passingRank
          rushingRank
          receivingRank
          defenseRank
          overallPercentile
          passingPercentile
          rushingPercentile
          receivingPercentile
          defensePercentile
          rushingYardsPerRush
          rushingPower
          rushingExplosiveness
          rushingEfficiency
          rushingUtilization
          passingInt
          passingTouchdowns
          passingExplosiveness
          passingEfficiency
          passingUtilization
          receivingImpactfulness
          receivingExplosiveness
          receivingReliability
          receivingUtilization
          defenseOverallPpi
          defenseOverallSafetyPpi
          defenseOverallCornerPpi
          defenseOverallOlbPpi
          defenseOverallMlbPpi
          defenseOverallOlbEdgePpi
          defenseOverallIlPpi
          runDefensePpi
          runDefenseSafetyPpi
          runDefenseCornerPpi
          runDefenseOlbPpi
          runDefenseMlbPpi
          runDefenseOlbEdgePpi
          runDefenseIlPpi
          passRushPpi
          passRushSafetyPpi
          passRushCornerPpi
          passRushOlbPpi
          passRushMlbPpi
          passRushOlbEdgePpi
          passRushIlPpi
          passCoveragePpi
          passCoverageSafetyPpi
          passCoverageCornerPpi
          passCoverageOlbPpi
          passCoverageMlbPpi
          passCoverageOlbEdgePpi
          passCoverageIlPpi
          plays
          pctPlaySafety
          pctPlayCorner
          pctPlayOlb
          pctPlayMlb
          pctPlayOlbEdge
          pctPlayIl
          versatilityIndex
          ctPasserRatingAgainst
          ctPassYardsAgainst
          ctTouchdownsAgainst
          ctInterceptions
          ctCompletionsAgainst
          ctAttemptsAgainst
          ctCompletionPercentageAgainst
          ctCpoe
          ctPressureRate
          ctSackRate
          ctHurryRate
          ctInterceptionRate
          ctOverallTackleRate
          ctCoverageTackleRate
          ctRunDefenseTackleRate
          ctPassRushTackleRate
          ctOverallMissedTackleRate
          ctCoverageMissedTackleRate
          ctRunDefenseMissedTackleRate
          ctPassRushMissedTackleRate
          ctOverallStopRate
          ctCoverageStopRate
          ctRunDefenseStopRate
          ctPassRushStopRate
          blockingTotalCount
          blockingRank
          blockingPercentile
          passBlockPpi
          runBlockPpi
          blockingSnaps
          blockingOverallPpi          
          pfRushingTotalPlayers
          pfRushingBetterScores
          pfRushingBestOffense
          pfRushingHardestDefense
          pfPassingTotalPlayers
          pfPassingBetterScores
          pfPassingBestOffense
          pfPassingHardestDefense
          pfReceivingTotalPlayers
          pfReceivingBetterScores
          pfReceivingBestOffense
          pfReceivingHardestDefense
          pfDefensiveTotalPlayers
          pfDefensiveHardestOffense
          pfDefensiveBestDefense
          pfBlockingTotalPlayers
          pfBlockingBestOffense
          pfBlockingHardestDefense
          tfOffensiveHardestDefense
          tfOffensiveBestOffense
          tfOffensiveTotalTeams
          tfDefensiveHardestOffense
          tfDefensiveBestDefense
          tfDefensiveTotalTeams
        }
      }
    `)
      .then((data:any) => data?.getPPIStats as UpdatedPlayerPPIStat)
      .then((ppiStats:UpdatedPlayerPPIStat) => {
        setPPIStats(ppiStats);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  function fetchNFLPPIStats () {
    if (!player?.id || !collegeTeamId || !nflTeamId || !position) return;
    if (!loading) setLoading(true);

    gql(`
      getPPIStatsNFL (
        playerId: ${player.id},
        compareToCollegeTeamId: ${collegeTeamId},
        compareToNFLTeamId: ${nflTeamId},
        positionCode: "${position}",
        categories: [${categoriesQueryValue}]
      ) {
        yearlyStats {
          year
          overallPPI ${PPIStats}
          passingPPI ${PPIStats}
          receivingPPI ${PPIStats}
          rushingPPI ${PPIStats}
          blockPPI ${PPIStats}
          defensePPI ${PPIStats}

          position
          overallTotalCount
          passingTotalCount
          rushingTotalCount
          receivingTotalCount
          defenseTotalCount
          overallRank
          passingRank
          rushingRank
          receivingRank
          defenseRank
          overallPercentile
          passingPercentile
          rushingPercentile
          receivingPercentile
          defensePercentile
          rushingYardsPerRush
          rushingPower
          rushingExplosiveness
          rushingEfficiency
          rushingUtilization
          passingInt
          passingTouchdowns
          passingExplosiveness
          passingEfficiency
          passingUtilization
          receivingImpactfulness
          receivingExplosiveness
          receivingReliability
          receivingUtilization
          defenseOverallPpi
          defenseOverallSafetyPpi
          defenseOverallCornerPpi
          defenseOverallOlbPpi
          defenseOverallMlbPpi
          defenseOverallOlbEdgePpi
          defenseOverallIlPpi
          runDefensePpi
          runDefenseSafetyPpi
          runDefenseCornerPpi
          runDefenseOlbPpi
          runDefenseMlbPpi
          runDefenseOlbEdgePpi
          runDefenseIlPpi
          passRushPpi
          passRushSafetyPpi
          passRushCornerPpi
          passRushOlbPpi
          passRushMlbPpi
          passRushOlbEdgePpi
          passRushIlPpi
          passCoveragePpi
          passCoverageSafetyPpi
          passCoverageCornerPpi
          passCoverageOlbPpi
          passCoverageMlbPpi
          passCoverageOlbEdgePpi
          passCoverageIlPpi
          plays
          pctPlaySafety
          pctPlayCorner
          pctPlayOlb
          pctPlayMlb
          pctPlayOlbEdge
          pctPlayIl
          versatilityIndex
          ctPasserRatingAgainst
          ctPassYardsAgainst
          ctTouchdownsAgainst
          ctInterceptions
          ctCompletionsAgainst
          ctAttemptsAgainst
          ctCompletionPercentageAgainst
          ctCpoe
          ctPressureRate
          ctSackRate
          ctHurryRate
          ctInterceptionRate
          ctOverallTackleRate
          ctCoverageTackleRate
          ctRunDefenseTackleRate
          ctPassRushTackleRate
          ctOverallMissedTackleRate
          ctCoverageMissedTackleRate
          ctRunDefenseMissedTackleRate
          ctPassRushMissedTackleRate
          ctOverallStopRate
          ctCoverageStopRate
          ctRunDefenseStopRate
          ctPassRushStopRate
          blockingTotalCount
          blockingRank
          blockingPercentile
          passBlockPpi
          runBlockPpi
          blockingSnaps
          blockingOverallPpi          
          pfRushingTotalPlayers
          pfRushingBetterScores
          pfRushingBestOffense
          pfRushingHardestDefense
          pfPassingTotalPlayers
          pfPassingBetterScores
          pfPassingBestOffense
          pfPassingHardestDefense
          pfReceivingTotalPlayers
          pfReceivingBetterScores
          pfReceivingBestOffense
          pfReceivingHardestDefense
          pfDefensiveTotalPlayers
          pfDefensiveHardestOffense
          pfDefensiveBestDefense
          pfBlockingTotalPlayers
          pfBlockingBestOffense
          pfBlockingHardestDefense
          tfOffensiveHardestDefense
          tfOffensiveBestOffense
          tfOffensiveTotalTeams
          tfDefensiveHardestOffense
          tfDefensiveBestDefense
          tfDefensiveTotalTeams
        }
      }
    `)
      .then((data:any) => data?.getPPIStatsNFL as UpdatedPlayerPPIStat)
      .then((ppiStats:UpdatedPlayerPPIStat) => {
        setPPIStats(ppiStats);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  function onUpdateProfileStatsCategories (selectedCategories:string[]) {
    const normalizedCategories = getNormalizedProfileStatsCategories(selectedCategories, isNFL);
    setColumns(categoriesToColumns(normalizedCategories, isNFL));
    dispatch((isNFL ? updateUserProfileStatsCategoriesNFL(normalizedCategories) : updateUserProfileStatsCategories(normalizedCategories)) as any);
  }

  return (
    <>
      <div id='ppi'>
        <UpdatedPlayerCharts
          selectedTab={selectedTab}
          loading={loading}
          player={player}
          collegeTeam={collegeTeam}
          nflTeam={nflTeam}
          position={position}
          paiStats={paiStats}
          combineStats={selectedTabValue === TABS_VALUES_ENUM.PRO_COMBINE ? proCombineStats : combineStats}
          combineEvents={selectedTabValue === TABS_VALUES_ENUM.PRO_COMBINE ? nflCombineEvents : combineEvents}
          ppiStats={ppiStats}
          isNFL={isNFL}
          hasPaiData={hasPaiData}
          hasCombineScore={hasCombineScore}
          hasProCombineData={hasProCombineData}
          hasHSCombineData={hasHSCombineData}
          hasPPIScore={hasPPIScore}
          onTabChange={(tab:number) => setSelectedTab(tab)}
          selectedTeamType={selectedTeamType}
          setSelectedTabValue={setSelectedTabValue}
          selectedTabValue={selectedTabValue}
          columns={columns}
          profileStatsCategoriesOptions={profileStatsCategoriesOptions}
          profileStatsCategories={normalizedProfileStatsCategories}
          onUpdateProfileStatsCategories={onUpdateProfileStatsCategories}
        />
      </div>

      {(selectedTabValue == TABS_VALUES_ENUM.PAI) && (
        <UpdatedPlayerTrackAndFieldData
          loading={loading}
          player={player}
          collegeTeam={collegeTeam}
          nflTeam={nflTeam}
          position={position}
          sprints={sprints}
          relays={relays}
          hurdles={hurdles}
          jumps={jumps}
          throws={throws}
          columns={columns}
          isNFL={isNFL}
        />
      )}

      {(selectedTabValue === TABS_VALUES_ENUM.COMBINE && hasCombineScore) && (
        <UpdatedPlayerCombineData
          loading={loading}
          player={player}
          collegeTeam={collegeTeam}
          nflTeam={nflTeam}
          position={position}
          combineEvents={combineEvents}
          columns={columns}
          isNFL={isNFL}
        />
      )}

      {(selectedTabValue === TABS_VALUES_ENUM.PRO_COMBINE && hasProCombineData) && (
        <UpdatedPlayerProCombineData
          loading={loading}
          player={player}
          collegeTeam={collegeTeam}
          nflTeam={nflTeam}
          position={position}
          nflCombineEvents={nflCombineEvents}
          columns={columns}
          isNFL={isNFL}
        />
      )}

      {(selectedTabValue === TABS_VALUES_ENUM.MULTISPORT) && (
        <UpdatedPlayerMultiSportData
          player={player}
          collegeTeam={collegeTeam}
          nflTeam={nflTeam}
          position={position}
          columns={columns}
          isNFL={isNFL}
          categories={normalizedProfileStatsCategories}
        />
      )}
    </>
  );
}
